import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Sensor } from '@/types';
import { Radio, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SensorFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensor?: Sensor | null;
  // CAMBIO v0.2.0: empresas en lugar de jardines
  companies?: { id: string; name: string }[];
  // Servidores MQTT y LNS para selección
  mqttServers?: { id: string; name: string; broker: string; port: number }[];
  lnsServers?: { id: string; name: string; serverUrl: string }[];
  onSave: (sensor: Sensor) => void;
  isLoading?: boolean;
  // Rol del usuario actual
  userRole?: 'superadmin' | 'company_admin';
}

// Custom native select component for better compatibility (igual que en ControllerFormDialog y GardenFormDialog)
interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}

function NativeSelect({ value, onChange, placeholder, options, className, disabled }: NativeSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer",
          !value && "text-muted-foreground",
          className
        )}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
    </div>
  );
}

export function SensorFormDialog({
  open,
  onOpenChange,
  sensor,
  companies = [],
  mqttServers = [],
  lnsServers = [],
  onSave,
  isLoading = false,
  userRole = 'company_admin',
}: SensorFormDialogProps) {
  const isEditing = !!sensor;
  const isSuperadmin = userRole === 'superadmin';
  
  const [name, setName] = useState('');
  const [deviceEUI, setDeviceEUI] = useState('');
  // CAMBIO v0.2.0: companyId en lugar de gardenId
  const [companyId, setCompanyId] = useState('');
  const [communicationType, setCommunicationType] = useState<'mqtt' | 'chirpstack'>('chirpstack');
  const [mqttServerId, setMqttServerId] = useState('');
  const [mqttTopic, setMqttTopic] = useState('');
  const [lnsServerId, setLnsServerId] = useState('');
  const [appKey, setAppKey] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  useEffect(() => {
    if (sensor) {
      setName(sensor.name);
      setDeviceEUI(sensor.deviceEUI);
      setCompanyId(sensor.companyId || '');
      setCommunicationType(sensor.communicationType);
      setMqttServerId(sensor.mqttConfig?.serverId || '');
      setMqttTopic(sensor.mqttConfig?.topic || '');
      setLnsServerId(sensor.lnsConfig?.serverId || '');
      setAppKey((sensor as any).appKey || '');
      setLat(sensor.coordinates?.lat.toString() || '');
      setLng(sensor.coordinates?.lng.toString() || '');
    } else {
      setName('');
      setDeviceEUI('');
      setCompanyId('');
      setCommunicationType('chirpstack');
      setMqttServerId('');
      setMqttTopic('');
      setLnsServerId('');
      setAppKey('');
      setLat('');
      setLng('');
    }
  }, [sensor, open]);

  // Debug: verificar que los servidores se están cargando
  useEffect(() => {
    if (open) {
      console.log('[SensorFormDialog] Servers loaded:', {
        communicationType,
        mqttServersCount: mqttServers.length,
        lnsServersCount: lnsServers.length,
        mqttServers: mqttServers.map(s => ({ id: s.id, name: s.name, broker: s.broker })),
        lnsServers: lnsServers.map(s => ({ id: s.id, name: s.name, serverUrl: s.serverUrl })),
      });
    }
  }, [open, communicationType, mqttServers, lnsServers]);

  const handleSubmit = () => {
    const now = new Date();
    
    // CAMBIO v0.2.0: Usar companyId en lugar de gardenId
    const newSensor: Sensor = {
      id: sensor?.id || `sensor-${Date.now()}`,
      name,
      model: 'EM500-SMTC',
      deviceEUI,
      companyId: companyId || undefined,
      communicationType,
      mqttConfig: communicationType === 'mqtt' && (mqttServerId || mqttTopic.trim()) ? {
        serverId: mqttServerId || undefined,
        topic: mqttTopic.trim() || undefined,
      } : undefined,
      lnsConfig: communicationType === 'chirpstack' && lnsServerId ? {
        serverId: lnsServerId,
      } : undefined,
      coordinates: lat && lng ? {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
      } : undefined,
      status: sensor?.status || 'offline',
      battery: sensor?.battery || 100,
      lastSeen: sensor?.lastSeen,
      lastReading: sensor?.lastReading,
      createdAt: sensor?.createdAt || now,
      updatedAt: now,
    };

    // Añadir appKey si está presente y es ChirpStack
    if (communicationType === 'chirpstack' && appKey.trim()) {
      (newSensor as any).appKey = appKey.trim().toUpperCase();
    }

    onSave(newSensor);
    onOpenChange(false);
  };

  // Validar según tipo de comunicación
  const isValid = name.trim() && deviceEUI.trim() && 
    (communicationType === 'chirpstack' 
      ? lnsServerId.trim() !== ''
      : (communicationType === 'mqtt' && mqttServerId.trim() !== '' && mqttTopic.trim() !== ''));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Radio className="w-5 h-5 text-info" />
            {isEditing ? 'Editar Sensor' : 'Nuevo Sensor'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Modifica los datos del sensor EM500-SMTC'
              : 'Añade un nuevo sensor de humedad y temperatura'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre del sensor *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Sensor Césped Principal"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="deviceEUI">DevEUI *</Label>
            <Input
              id="deviceEUI"
              value={deviceEUI}
              onChange={(e) => setDeviceEUI(e.target.value.toUpperCase())}
              placeholder="Ej: B8404136C1842E5A"
              maxLength={16}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Identificador único del dispositivo LoRaWAN (16 caracteres hexadecimales)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Empresa</Label>
              <NativeSelect
                value={companyId || 'none'}
                onChange={(value) => setCompanyId(value === 'none' ? '' : value)}
                placeholder="Seleccionar empresa (opcional)"
                disabled={!isSuperadmin}
                options={[
                  { value: 'none', label: 'Sin empresa asignada' },
                  ...companies.map((company) => ({
                    value: company.id,
                    label: company.name,
                  })),
                ]}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="communication">Tipo de comunicación *</Label>
              <NativeSelect
                value={communicationType}
                onChange={(value) => setCommunicationType(value as 'mqtt' | 'chirpstack')}
                placeholder="Seleccionar tipo"
                options={[
                  { value: 'chirpstack', label: 'ChirpStack (LNS)' },
                  { value: 'mqtt', label: 'MQTT Directo' },
                ]}
              />
            </div>
          </div>

          {/* Campos MQTT - solo visibles cuando se selecciona MQTT */}
          {communicationType === 'mqtt' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="mqttServer">Servidor MQTT *</Label>
                <NativeSelect
                  value={mqttServerId}
                  onChange={setMqttServerId}
                  placeholder="Seleccionar servidor MQTT"
                  options={[
                    { value: '', label: 'Seleccionar servidor MQTT' },
                    ...mqttServers.map((server) => ({
                      value: server.id,
                      label: `${server.name} (${server.broker}:${server.port})`,
                    })),
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mqttTopic">Tópico MQTT de suscripción *</Label>
                <Input
                  id="mqttTopic"
                  value={mqttTopic}
                  onChange={(e) => setMqttTopic(e.target.value)}
                  placeholder="Ej: sensors/em500/b8404136c1842e5a/data"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Tópico al que el sistema se suscribirá para recibir los datos del sensor
                </p>
              </div>
            </>
          )}

          {/* Campos ChirpStack - solo visibles cuando se selecciona ChirpStack */}
          {communicationType === 'chirpstack' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="lnsServer">Servidor LNS (ChirpStack) *</Label>
                <NativeSelect
                  value={lnsServerId}
                  onChange={setLnsServerId}
                  placeholder="Seleccionar servidor LNS"
                  options={[
                    { value: '', label: 'Seleccionar servidor LNS' },
                    ...lnsServers.map((server) => ({
                      value: server.id,
                      label: `${server.name} (${server.serverUrl})`,
                    })),
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="appKey">AppKey (opcional)</Label>
                <Input
                  id="appKey"
                  value={appKey}
                  onChange={(e) => setAppKey(e.target.value.toUpperCase())}
                  placeholder="AABBCCDDEEFF00112233445566778899"
                  maxLength={32}
                  className="font-mono text-sm uppercase"
                />
                <p className="text-xs text-muted-foreground">
                  32 caracteres hexadecimales. Si se deja vacío, se generará automáticamente.
                </p>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label>Coordenadas (opcional)</Label>
            <div className="grid grid-cols-2 gap-4">
              <Input
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="Latitud"
                type="number"
                step="0.0001"
              />
              <Input
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                placeholder="Longitud"
                type="number"
                step="0.0001"
              />
            </div>
          </div>

          {/* Model info */}
          <div className="p-3 rounded-lg bg-muted/50 text-sm">
            <p className="font-medium mb-1">Modelo: EM500-SMTC</p>
            <p className="text-muted-foreground text-xs">
              Sensor de suelo que mide temperatura, humedad y conductividad eléctrica. 
              Comunicación LoRaWAN con batería de larga duración.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isLoading}>
            {isLoading ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Sensor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
